import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Container } from '@/components/ui/container';
import { Check, Clock, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import AdComponent from '@/components/ads/AdComponent';

const EmailConfirmation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [message, setMessage] = useState('Po verifikohet email-i juaj...');

  const token = searchParams.get('token');
  const type = searchParams.get('type');

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        if (!token || type !== 'email_confirmation') {
          setStatus('error');
          setMessage('Lidhja e verifikimit është e pavlefshme. Ju lutemi kontrolloni email-in ose kërkoni një lidhje të re verifikimi.');
          return;
        }

        // Përpiquni të verifikoni email-in me Supabase
        const { error } = await supabase.auth.verifyOtp({
          token_hash: token,
          type: 'email',
        });

        if (error) {
          setStatus('error');
          setMessage(error.message || 'Verifikimi i email-it dështoi. Lidhja mund të ketë skaduar.');
        } else {
          setStatus('success');
          setMessage('Email-i juaj u verifikua me sukses!');

          // Përditësoni profilin e përdoruesit nëse është e nevojshme
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            // Përdoruesi u verifikua, mund të shtohen veprime të tjera këtu
          }
        }
      } catch (error) {
        setStatus('error');
        setMessage('Ndodhi një gabim i papritur. Ju lutemi provoni përsëri më vonë.');
      }
    };

    verifyEmail();
  }, [token, type]);

  const handleContinue = () => {
    navigate('/profile');
  };

  const handleResendVerification = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (user && user.email) {
        const { error } = await supabase.auth.resend({
          type: 'signup',
          email: user.email,
        });

        if (error) {
          setMessage('Dërgimi i email-it të ri të verifikimit dështoi. Ju lutemi provoni përsëri më vonë.');
        } else {
          setMessage('Një email i ri verifikimi u dërgua. Ju lutemi kontrolloni inbox-in.');
        }
      } else {
        setMessage('Ju lutemi bëni login fillimisht që të mund të dërgoni përsëri email-in e verifikimit.');
      }
    } catch (error) {
      setMessage('Ndodhi një gabim i papritur. Ju lutemi provoni përsëri më vonë.');
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />

      <Container className="flex-grow py-16">
        <div className="max-w-md mx-auto">
          <Card className="border shadow-md">
            <CardHeader>
              <div className="flex justify-center mb-4">
                {status === 'verifying' && <Clock className="h-12 w-12 text-blue-500 animate-pulse" />}
                {status === 'success' && <Check className="h-12 w-12 text-green-500" />}
                {status === 'error' && <AlertTriangle className="h-12 w-12 text-red-500" />}
              </div>

              <CardTitle className="text-center text-xl">
                {status === 'verifying' && 'Po verifikohet Email-i'}
                {status === 'success' && 'Email-i u verifikua'}
                {status === 'error' && 'Verifikimi Dështoi'}
              </CardTitle>

              <CardDescription className="text-center">{message}</CardDescription>
            </CardHeader>

            <CardContent>
              {/* AdSense ad */}
              <AdComponent adSlot="3456789012" adFormat="rectangle" />

              {status === 'success' && (
                <p className="text-center text-green-600 mt-4">
                  Faleminderit që verifikuat email-in! Tani mund të përdorni të gjitha funksionet e platformës sonë.
                </p>
              )}

              {status === 'error' && (
                <p className="text-center text-gray-600 mt-4">
                  Nuk mundëm të verifikojmë email-in tuaj. Kjo mund të ndodhë sepse lidhja ka skaduar ose është e pavlefshme.
                </p>
              )}
            </CardContent>

            <CardFooter className="flex flex-col gap-2">
              {status === 'success' && (
                <Button className="w-full" onClick={handleContinue}>
                  Vazhdo te Profili Juaj
                </Button>
              )}

              {status === 'error' && (
                <Button className="w-full" onClick={handleResendVerification}>
                  Dërgo Email-in e Verifikimit Përsëri
                </Button>
              )}

              <Button variant="outline" className="w-full" onClick={() => navigate('/')}>
                Kthehu te Faqja Kryesore
              </Button>
            </CardFooter>
          </Card>
        </div>
      </Container>

      <Footer />
    </div>
  );
};

export default EmailConfirmation;
